import cleanPing from '@/assets/notification-sounds/clean-ping.mp3'
import duo from '@/assets/notification-sounds/duo.mp3'
import friendlyDoubleNote from '@/assets/notification-sounds/friendly-double-note.mp3'
import pop from '@/assets/notification-sounds/pop.mp3'
import premiumGlassChime from '@/assets/notification-sounds/premium-glass-chime.mp3'
import ringtone from '@/assets/notification-sounds/ringtone.mp3'
import rise from '@/assets/notification-sounds/rise.mp3'

type WindowWithWebkitAudioContext = Window & {
  webkitAudioContext?: typeof AudioContext
}

export interface NotificationSoundPreset {
  id: string
  label: string
  /** Bundled audio asset URL (hashed by Vite). */
  src: string
}

/**
 * Selectable notification chimes. Short (< 1s) audio files bundled with the
 * app; the first entry is the default. Ids are persisted in the store, so keep
 * them stable.
 */
export const NOTIFICATION_SOUNDS = [
  { id: 'clean-ping', label: 'Clean ping', src: cleanPing },
  { id: 'friendly-double-note', label: 'Friendly double note', src: friendlyDoubleNote },
  { id: 'premium-glass-chime', label: 'Premium glass chime', src: premiumGlassChime },
  { id: 'duo', label: 'Duo', src: duo },
  { id: 'pop', label: 'Pop', src: pop },
  { id: 'rise', label: 'Rise', src: rise },
] as const satisfies readonly NotificationSoundPreset[]

export type NotificationSoundId = (typeof NOTIFICATION_SOUNDS)[number]['id']

export const DEFAULT_NOTIFICATION_SOUND_ID: NotificationSoundId = NOTIFICATION_SOUNDS[0].id

function resolveSound(soundId: NotificationSoundId): NotificationSoundPreset {
  return NOTIFICATION_SOUNDS.find((sound) => sound.id === soundId) ?? NOTIFICATION_SOUNDS[0]
}

/** Play notifications a touch below full scale so they accent rather than blast. */
const NOTIFICATION_VOLUME = 0.7

let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext
  if (typeof window === 'undefined') return null

  const AudioContextConstructor =
    window.AudioContext ?? (window as WindowWithWebkitAudioContext).webkitAudioContext
  if (!AudioContextConstructor) return null

  audioContext = new AudioContextConstructor()
  return audioContext
}

// Decoded buffers cached per source so each file is fetched + decoded once.
const bufferBySrc = new Map<string, AudioBuffer>()
const loadBySrc = new Map<string, Promise<AudioBuffer | null>>()

function loadBuffer(context: AudioContext, src: string): Promise<AudioBuffer | null> {
  const cached = bufferBySrc.get(src)
  if (cached) return Promise.resolve(cached)

  let load = loadBySrc.get(src)
  if (!load) {
    load = fetch(src)
      .then((response) => response.arrayBuffer())
      .then((data) => context.decodeAudioData(data))
      .then((buffer) => {
        bufferBySrc.set(src, buffer)
        return buffer
      })
      .catch(() => null)
    loadBySrc.set(src, load)
  }
  return load
}

/**
 * Prime Web Audio on the first user gesture so later realtime events may play,
 * and warm the ringtone so the first incoming call rings without delay.
 * Notification chimes stay lazy: only the selected sound is fetched when a
 * notification arrives or the user previews it in Settings.
 */
export function prepareNotificationSound(): () => void {
  if (typeof window === 'undefined') return () => undefined

  const unlock = () => {
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
    try {
      const context = getAudioContext()
      if (!context) return
      if (context.state === 'suspended') void context.resume().catch(() => undefined)
      void loadBuffer(context, ringtone)
    } catch {
      // Web Audio may be unavailable or blocked in this browser.
    }
  }

  window.addEventListener('pointerdown', unlock, { once: true })
  window.addEventListener('keydown', unlock, { once: true })

  return () => {
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
  }
}

/** Resolve the audio context, resuming it if the browser suspended it. */
async function getPlayableContext(): Promise<AudioContext | null> {
  const context = getAudioContext()
  if (!context) return null
  if (context.state === 'suspended') {
    try {
      await context.resume()
    } catch {
      return null // Autoplay still blocked — skip silently.
    }
  }
  return context
}

/** Wire a buffer through a gain node to the speakers, ready to start. */
function connectSource(context: AudioContext, buffer: AudioBuffer, volume: number) {
  const source = context.createBufferSource()
  source.buffer = buffer
  const gain = context.createGain()
  gain.gain.value = volume
  source.connect(gain)
  gain.connect(context.destination)
  return { source, gain }
}

/**
 * Play a short, unobtrusive chime for foreground notifications.
 *
 * Browsers can block audio until the user has interacted with the page. A
 * blocked or unavailable audio context is intentionally ignored so receiving
 * a notification can never interrupt the realtime subscription.
 */
export function playNotificationSound(soundId: NotificationSoundId = DEFAULT_NOTIFICATION_SOUND_ID): void {
  const sound = resolveSound(soundId)

  const play = async () => {
    const context = await getPlayableContext()
    if (!context) return
    const buffer = await loadBuffer(context, sound.src)
    if (!buffer) return
    connectSource(context, buffer, NOTIFICATION_VOLUME).source.start()
  }

  // Audio is an enhancement; notification delivery must remain unaffected.
  void play().catch(() => undefined)
}

/**
 * The incoming-call ring. Not in NOTIFICATION_SOUNDS on purpose — it is a
 * loop, not a chime, and must never appear in the Settings sound picker.
 */
const RINGTONE_VOLUME = 0.5

interface ActiveRing {
  /** A no-op until the loop is audibly playing (the buffer load is async). */
  stop: () => void
}

let activeRing: ActiveRing | null = null

/**
 * Start the looping incoming-call ring. Idempotent — a second call while a
 * ring is live is a no-op. The caller decides when ringing is appropriate
 * (sound preference, not already in a call); this only makes noise.
 *
 * Same autoplay caveat as playNotificationSound: before the first user
 * gesture in the tab the context cannot resume, so the ring is silently
 * skipped — the incoming-call popup still shows.
 */
export function startRingtone(): void {
  if (activeRing) return

  const ring: ActiveRing = { stop: () => undefined }
  activeRing = ring

  const start = async () => {
    const context = await getPlayableContext()
    if (!context) return
    const buffer = await loadBuffer(context, ringtone)
    // stopRingtone may have raced the async load — never start a loop nobody
    // holds a stop handle for.
    if (!buffer || ring !== activeRing) return

    const { source, gain } = connectSource(context, buffer, RINGTONE_VOLUME)
    source.loop = true
    ring.stop = () => {
      try {
        source.stop()
      } catch {
        // Already stopped.
      }
      source.disconnect()
      gain.disconnect()
    }
    source.start()
  }

  void start().catch(() => undefined)
}

/** Stop the ring. Safe to call at any time, including while it is loading. */
export function stopRingtone(): void {
  if (!activeRing) return
  const ring = activeRing
  activeRing = null
  ring.stop()
}
